import React, { useEffect, useState } from 'react';
import { IconButton, TextField, Tooltip } from '@mui/material';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { Card, CardBody, TableScroll } from '../../PanelComerciales.styles';
import SectionHeader from '../SectionHeader/SectionHeader';
import Pagination from '../Pagination/Pagination';
import MergeDealsModal from '../DealsStatus/MergeDealsModal';
import { Commercial } from '../../PanelComerciales.types';
import { panelTheme } from '../../PanelComerciales.theme';
import {
    DealMergePayload,
    DuplicateGroup,
    DuplicatesPage,
    DuplicatesSearch,
} from 'src/redux/panel_comerciales/panel_comerciales.types';

/** ms de espera tras teclear antes de disparar la búsqueda en backend. */
const SEARCH_DEBOUNCE_MS = 400;

interface DuplicatesSectionProps {
    page: DuplicatesPage;
    loading: boolean;
    search: DuplicatesSearch;
    commercials: Commercial[];
    actionLoading: boolean;
    onSearch: (search: DuplicatesSearch) => void;
    onLoadPage: (offset: number) => void;
    onMerge: (payload: DealMergePayload) => void | Promise<void>;
}

const EMPTY_CELL: React.CSSProperties = {
    textAlign: 'center',
    padding: 24,
    color: '#9CA3AF',
};

const groupOwners = (group: DuplicateGroup): string => {
    const names = new Set(group.deals.map((d) => d.ownerName ?? 'Sin comercial'));
    return Array.from(names).join(', ');
};

/**
 * Monitor de deals duplicados (sección propia, ago-2026): búsqueda por correo
 * y comercial implicado (sobre el conjunto completo, backend), tabla paginada
 * de grupos y acción de merge por grupo.
 */
const DuplicatesSection: React.FC<DuplicatesSectionProps> = ({
    page,
    loading,
    search,
    commercials,
    actionLoading,
    onSearch,
    onLoadPage,
    onMerge,
}) => {
    const [mergingGroup, setMergingGroup] = useState<DuplicateGroup | null>(null);
    const [emailInput, setEmailInput] = useState(search.email);
    const [ownerInput, setOwnerInput] = useState(search.owner);

    // Debounce: al teclear, esperar antes de pedir al backend (evita una
    // llamada por tecla). Si el input coincide con la búsqueda vigente no
    // redispara (p. ej. tras un refresco externo).
    useEffect(() => {
        if (emailInput === search.email && ownerInput === search.owner) return;
        const id = setTimeout(
            () => onSearch({ email: emailInput, owner: ownerInput }),
            SEARCH_DEBOUNCE_MS,
        );
        return () => clearTimeout(id);
    }, [emailInput, ownerInput, search.email, search.owner, onSearch]);

    const handleMerge = async (payload: DealMergePayload) => {
        await onMerge(payload);
        setMergingGroup(null);
    };

    return (
        <Card>
            <SectionHeader
                title='Deals duplicados'
                subtitle='Clientes con más de un deal abierto (por id o correo).'
                icon={<CallMergeIcon style={{ color: panelTheme.colors.indigo }} />}
            />
            <CardBody>
                <div
                    style={{
                        display: 'flex',
                        gap: 12,
                        flexWrap: 'wrap',
                        marginBottom: 12,
                    }}
                >
                    <TextField
                        size='small'
                        label='Buscar por correo'
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <SearchOutlinedIcon
                                    fontSize='small'
                                    style={{ color: '#9CA3AF', marginRight: 4 }}
                                />
                            ),
                        }}
                        sx={{ minWidth: 220 }}
                    />
                    <TextField
                        size='small'
                        label='Buscar por comercial'
                        value={ownerInput}
                        onChange={(e) => setOwnerInput(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <SearchOutlinedIcon
                                    fontSize='small'
                                    style={{ color: '#9CA3AF', marginRight: 4 }}
                                />
                            ),
                        }}
                        sx={{ minWidth: 220 }}
                    />
                    <span style={{ marginLeft: 'auto' }}>
                        <Pagination
                            total={page.total}
                            limit={page.limit}
                            offset={page.offset}
                            onChange={onLoadPage}
                            disabled={loading}
                            label='de duplicados'
                        />
                    </span>
                </div>
                <TableScroll>
                    <table>
                        <thead>
                            <tr>
                                <th>Cliente</th>
                                <th>Correo</th>
                                <th>Deals abiertos</th>
                                <th>Comerciales implicados</th>
                                <th style={{ textAlign: 'right' }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {page.groups.map((group) => (
                                <tr key={group.clientKey}>
                                    <td>
                                        <strong>
                                            {group.idPcom !== null
                                                ? `Cliente ${group.idPcom}`
                                                : 'Cliente por correo'}
                                        </strong>
                                    </td>
                                    <td>{group.clientEmail ?? '—'}</td>
                                    <td>
                                        {group.deals.map((d) => `#${d.dealId}`).join(', ')}
                                    </td>
                                    <td>{groupOwners(group)}</td>
                                    <td style={{ textAlign: 'right' }}>
                                        <Tooltip title='Resolver duplicado (merge)'>
                                            <IconButton
                                                size='small'
                                                aria-label='Resolver duplicado'
                                                onClick={() => setMergingGroup(group)}
                                            >
                                                <CallMergeIcon fontSize='small' />
                                            </IconButton>
                                        </Tooltip>
                                    </td>
                                </tr>
                            ))}
                            {page.groups.length === 0 && (
                                <tr>
                                    <td colSpan={5} style={EMPTY_CELL}>
                                        {search.email || search.owner
                                            ? 'Sin duplicados que coincidan con la búsqueda.'
                                            : 'Sin deals duplicados detectados.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </TableScroll>
            </CardBody>

            <MergeDealsModal
                group={mergingGroup}
                commercials={commercials}
                busy={actionLoading}
                onClose={() => setMergingGroup(null)}
                onConfirm={handleMerge}
            />
        </Card>
    );
};

export default DuplicatesSection;
